import React, { useMemo, useState } from "react";
import { format } from "date-fns";
import {
  Receipt, FileCheck, Mail, MessageSquare, Download, Trash2,
  ArrowRightLeft, Zap, Filter, ChevronRight
} from "lucide-react";
import { Virtuoso } from "react-virtuoso";
import { Button } from "../ui/button";

const STATUS_CONFIG = {
  draft:     { label: "Draft",     cls: "text-gray-500" },
  sent:      { label: "Sent",      cls: "text-blue-500" },
  paid:      { label: "Paid",      cls: "text-emerald-500" },
  partially_paid: { label: "Partially Paid", cls: "text-amber-500" },
  overdue:   { label: "Overdue",   cls: "text-red-500" },
  cancelled: { label: "Cancelled", cls: "text-gray-400" },
  accepted:  { label: "Accepted",  cls: "text-emerald-500" },
  declined:  { label: "Declined",  cls: "text-red-500" },
  converted: { label: "Converted", cls: "text-purple-500" },
};

const STATUS_FILTERS = {
  invoice: ["all", "draft", "sent", "partially_paid", "paid", "overdue", "cancelled"],
  estimate: ["all", "draft", "sent", "accepted", "declined", "converted"],
};

// Auto-virtualize when total visible doc count exceeds this many rows.
// Keeps small/medium accounts on the regular DOM path so animations / hover
// states / month-group accordion behaviour all remain identical.
const VIRTUALIZATION_THRESHOLD = 100;

const DocRow = ({
  doc, index, isDark, isEstimate, canCharge,
  onView, onEmail, onSendSms, onDownload, onDelete, onConvert, onCharge,
}) => {
  const st = STATUS_CONFIG[doc.status] || STATUS_CONFIG.draft;
  const isEst = (doc.document_type || "invoice") === "estimate";
  return (
    <div
      className={`group px-5 py-3.5 flex items-center justify-between cursor-pointer transition-colors ${
        isDark ? "hover:bg-white/[0.03]" : "hover:bg-gray-50/80"
      }`}
      onClick={() => onView(doc)}
      data-testid={`doc-row-${index}`}
    >
      <div className="flex items-center gap-3.5 min-w-0 flex-1">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
          isEst
            ? isDark ? "bg-orange-500/10" : "bg-orange-50"
            : isDark ? "bg-blue-500/10" : "bg-blue-50"
        }`}>
          {isEst
            ? <FileCheck className="w-4 h-4 text-orange-500" />
            : <Receipt className="w-4 h-4 text-blue-500" />
          }
        </div>
        <div className="min-w-0">
          <p className={`text-sm font-medium truncate ${isDark ? "text-white" : "text-gray-900"}`}>
            {doc.customer_name || "Unknown"}
          </p>
          <p className={`text-xs ${isDark ? "text-zinc-500" : "text-gray-400"}`}>
            {doc.invoice_number}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-shrink-0">
        {/* Quick actions - visible on hover */}
        <div className="hidden sm:flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-blue-500" onClick={(e) => { e.stopPropagation(); onEmail(doc); }} title="Email" data-testid={`email-${index}`}>
            <Mail className="w-3.5 h-3.5" />
          </Button>
          {onSendSms && (
            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-emerald-500" onClick={(e) => { e.stopPropagation(); onSendSms(doc); }} title="Send SMS" data-testid={`sms-${index}`}>
              <MessageSquare className="w-3.5 h-3.5" />
            </Button>
          )}
          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" onClick={(e) => { e.stopPropagation(); onDownload(doc); }} title="PDF" data-testid={`pdf-${index}`}>
            <Download className="w-3.5 h-3.5" />
          </Button>
          {isEst && doc.status !== "converted" && (
            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-purple-500" onClick={(e) => { e.stopPropagation(); onConvert(doc.invoice_id); }} title="Convert" data-testid={`convert-${index}`}>
              <ArrowRightLeft className="w-3.5 h-3.5" />
            </Button>
          )}
          {!isEst && doc.status !== "paid" && canCharge && (
            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-[#635BFF]" onClick={(e) => { e.stopPropagation(); onCharge(doc); }} title="Stripe" data-testid={`charge-${index}`}>
              <Zap className="w-3.5 h-3.5" />
            </Button>
          )}
          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-red-500" onClick={(e) => { e.stopPropagation(); onDelete(doc.invoice_id); }} title="Delete" data-testid={`delete-${index}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>

        <div className="text-right">
          <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
            ${(doc.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </p>
          <p className={`text-xs font-medium ${st.cls}`}>{st.label}</p>
          {doc.status === "partially_paid" && (
            <p className={`text-[10px] ${isDark ? "text-zinc-500" : "text-gray-400"}`} data-testid={`balance-${index}`}>
              Bal ${Math.max((doc.total || 0) - (doc.amount_paid || 0), 0).toFixed(2)}
            </p>
          )}
        </div>

        <ChevronRight className={`w-4 h-4 sm:hidden ${isDark ? "text-zinc-600" : "text-gray-300"}`} />
      </div>
    </div>
  );
  // (isEstimate is intentionally unused — kept in props signature for future styling overrides)
};

const MonthHeader = ({ label, isDark }) => (
  <div className={`px-5 py-3 flex items-center justify-between ${isDark ? "bg-zinc-900/40 border border-zinc-800 border-b-zinc-800" : "bg-white border border-gray-100 shadow-sm border-b-gray-100"} rounded-t-xl`}>
    <h3 className={`text-base font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>{label}</h3>
  </div>
);

const MonthFooter = ({ group, isDark, isEstimate }) => (
  <div className={`px-5 py-3 flex items-center justify-between rounded-b-xl ${isDark ? "bg-zinc-800/30 border border-zinc-800 border-t" : "bg-gray-50/80 border border-gray-100 border-t"}`}>
    <div className="flex items-center gap-2.5">
      <span className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold ${
        isEstimate
          ? isDark ? "bg-orange-500/15 text-orange-400" : "bg-orange-100 text-orange-600"
          : isDark ? "bg-blue-500/15 text-blue-400" : "bg-blue-100 text-blue-600"
      }`}>
        {group.count}
      </span>
      <span className={`text-sm font-medium ${isDark ? "text-zinc-300" : "text-gray-700"}`}>Total</span>
    </div>
    <span className={`text-sm font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
      ${group.total.toLocaleString('en-US', { minimumFractionDigits: 2 })}
    </span>
  </div>
);

const InvoiceDocList = ({
  documents = [],
  isEstimate = false,
  isDark,
  onView,
  onEdit, // eslint-disable-line no-unused-vars
  onEmail,
  onSendSms,
  onDownload,
  onDelete,
  onConvert,
  onCharge,
  connectStatus,
  // Server-side pagination — when the parent passes onEndReached we always
  // virtualise (regardless of size) so infinite scroll can fire as the
  // user reaches the bottom. `loadingMore` / `hasMore` drive the footer
  // affordance.
  onEndReached,
  loadingMore = false,
  hasMore = false,
}) => {
  const [statusFilter, setStatusFilter] = useState("all");
  const docType = isEstimate ? "estimate" : "invoice";
  const filters = STATUS_FILTERS[docType];
  const canCharge = connectStatus?.connected && connectStatus?.charges_enabled;

  // Group by month
  const grouped = useMemo(() => {
    let docs = documents;
    if (statusFilter !== "all") {
      docs = docs.filter(d => d.status === statusFilter);
    }

    const groups = {};
    for (const doc of docs) {
      const created = doc.created_at ? new Date(doc.created_at) : new Date();
      const key = format(created, "MMM yyyy");
      if (!groups[key]) groups[key] = { label: key, docs: [], total: 0, count: 0 };
      groups[key].docs.push(doc);
      groups[key].total += doc.total || 0;
      groups[key].count += 1;
    }
    return Object.values(groups);
  }, [documents, statusFilter]);

  // Flatten groups into a typed list of items: header → docs → footer.
  // Used only when virtualizing — preserves visual structure without measuring
  // each group separately. Rows have stable keys via composed ids.
  const flatItems = useMemo(() => {
    const items = [];
    for (const group of grouped) {
      items.push({ type: "header", group, key: `h-${group.label}` });
      for (let i = 0; i < group.docs.length; i++) {
        items.push({
          type: "doc",
          doc: group.docs[i],
          group,
          docIndex: i,
          key: `d-${group.label}-${group.docs[i].invoice_id}`,
        });
      }
      items.push({ type: "footer", group, key: `f-${group.label}` });
    }
    return items;
  }, [grouped]);

  const totalDocs = useMemo(
    () => grouped.reduce((acc, g) => acc + g.docs.length, 0),
    [grouped]
  );
  const useVirtual = !!onEndReached || totalDocs > VIRTUALIZATION_THRESHOLD;

  const renderFlatItem = (_, item) => {
    if (item.type === "header") return <MonthHeader label={item.group.label} isDark={isDark} />;
    if (item.type === "footer") return (
      <div className="pb-4">
        <MonthFooter group={item.group} isDark={isDark} isEstimate={isEstimate} />
      </div>
    );
    return (
      <div className={`${isDark ? "bg-zinc-900/40 border-l border-r border-zinc-800" : "bg-white border-l border-r border-gray-100 shadow-sm"}`}>
        <div className={`${isDark ? "border-t border-white/[0.06]" : "border-t border-black/[0.06]"} ${item.docIndex === 0 ? "border-t-0" : ""}`}>
          <DocRow
            doc={item.doc}
            index={item.docIndex}
            isDark={isDark}
            isEstimate={isEstimate}
            canCharge={canCharge}
            onView={onView}
            onEmail={onEmail}
            onSendSms={onSendSms}
            onDownload={onDownload}
            onDelete={onDelete}
            onConvert={onConvert}
            onCharge={onCharge}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="doc-list">
      {/* Status filter pills */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1" data-testid="status-filters">
        {filters.map(f => {
          const active = statusFilter === f;
          return (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize whitespace-nowrap transition-all ${
                active
                  ? isEstimate
                    ? "bg-orange-500 text-white shadow-sm"
                    : "bg-blue-500 text-white shadow-sm"
                  : isDark
                    ? "bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-300"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              data-testid={`filter-${f}`}
            >
              {f === "all" && <Filter className="w-3 h-3 inline mr-1" />}
              {f.replace(/_/g, " ")}
            </button>
          );
        })}
      </div>

      {/* Empty state */}
      {grouped.length === 0 && (
        <div className={`text-center py-16 rounded-xl ${isDark ? "bg-zinc-900/50" : "bg-gray-50"}`}>
          {isEstimate
            ? <FileCheck className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" />
            : <Receipt className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" />
          }
          <p className="text-muted-foreground text-sm">
            {statusFilter !== "all" ? `No ${statusFilter} ${docType}s found` : `No ${docType}s yet`}
          </p>
        </div>
      )}

      {/* Virtualized: large datasets OR when infinite-scroll is wired */}
      {grouped.length > 0 && useVirtual && (
        <Virtuoso
          useWindowScroll
          data={flatItems}
          computeItemKey={(_, item) => item.key}
          increaseViewportBy={{ top: 400, bottom: 800 }}
          itemContent={renderFlatItem}
          endReached={onEndReached}
          components={{
            Footer: () => (
              <div className="py-4 flex justify-center" data-testid="doc-list-footer">
                {loadingMore ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="w-3.5 h-3.5 rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground animate-spin" />
                    Loading more…
                  </div>
                ) : hasMore ? (
                  <div className="text-xs text-muted-foreground/60">Scroll for more</div>
                ) : null}
              </div>
            ),
          }}
          data-testid="doc-list-virtuoso"
        />
      )}

      {/* Regular: month groups, animated, normal DOM */}
      {grouped.length > 0 && !useVirtual && grouped.map((group) => (
        <div
          key={group.label}
          className={`rounded-xl overflow-hidden ${isDark ? "bg-zinc-900/40 border border-zinc-800" : "bg-white border border-gray-100 shadow-sm"}`}
          data-testid={`month-group-${group.label}`}
        >
          {/* Month header */}
          <div className={`px-5 py-3 flex items-center justify-between ${isDark ? "border-b border-zinc-800" : "border-b border-gray-100"}`}>
            <h3 className={`text-base font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>{group.label}</h3>
          </div>

          {/* Document rows */}
          <div className="divide-y" style={{ borderColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)" }}>
            {group.docs.map((doc, i) => (
              <DocRow
                key={doc.invoice_id}
                doc={doc}
                index={i}
                isDark={isDark}
                isEstimate={isEstimate}
                canCharge={canCharge}
                onView={onView}
                onEmail={onEmail}
                onSendSms={onSendSms}
                onDownload={onDownload}
                onDelete={onDelete}
                onConvert={onConvert}
                onCharge={onCharge}
              />
            ))}
          </div>

          {/* Month total footer */}
          {group.docs.length > 0 && (
            <div className={`px-5 py-3 flex items-center justify-between ${isDark ? "bg-zinc-800/30 border-t border-zinc-800" : "bg-gray-50/80 border-t border-gray-100"}`}>
              <div className="flex items-center gap-2.5">
                <span className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold ${
                  isEstimate
                    ? isDark ? "bg-orange-500/15 text-orange-400" : "bg-orange-100 text-orange-600"
                    : isDark ? "bg-blue-500/15 text-blue-400" : "bg-blue-100 text-blue-600"
                }`}>
                  {group.count}
                </span>
                <span className={`text-sm font-medium ${isDark ? "text-zinc-300" : "text-gray-700"}`}>Total</span>
              </div>
              <span className={`text-sm font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                ${group.total.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default InvoiceDocList;
